import { useQuery } from '@tanstack/react-query';
import { Linkedin, Users } from 'lucide-react';
import { useGql } from '@ah/design';
import { Header, PullToRefresh } from '@ah/design/mobile';
import { MY_CONNECTIONS_QUERY, type Connection } from '../shared/index.ts';

export function Connections() {
  const gql = useGql();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['connections'],
    queryFn: async () =>
      (await gql.request<{ myConnections: Connection[] }>(MY_CONNECTIONS_QUERY)).myConnections,
  });

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title="Connections" />
      <PullToRefresh className="flex-1 px-4 py-4" onRefresh={() => refetch()}>
        {isLoading ? (
          <ConnectionsSkeleton />
        ) : !data || data.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
            <Users size={32} className="text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">No connections yet</p>
            <p className="text-sm text-muted-foreground">
              You can only connect with people you meet at a Gathering.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border rounded-xl border border-border bg-card overflow-hidden">
            {data.map((c) => (
              <li key={c.id} className="flex items-center gap-3 px-3 py-3">
                {c.pictureUrl ? (
                  <img src={c.pictureUrl} alt="" className="w-10 h-10 rounded-full object-cover bg-muted" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-muted" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{c.name}</p>
                  {c.metAtGatheringTitle ? (
                    <p className="text-xs text-muted-foreground truncate">
                      Met at {c.metAtGatheringTitle}
                    </p>
                  ) : null}
                </div>
                {c.linkedinUrl ? (
                  <a
                    href={c.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground"
                    aria-label="Open LinkedIn"
                  >
                    <Linkedin size={18} />
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </PullToRefresh>
    </div>
  );
}

function ConnectionsSkeleton() {
  return (
    <ul className="divide-y divide-border rounded-xl border border-border bg-card overflow-hidden animate-pulse">
      {Array.from({ length: 6 }).map((_, i) => (
        <li key={i} className="flex items-center gap-3 px-3 py-3">
          <div className="w-10 h-10 rounded-full bg-muted" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-1/2 rounded bg-muted" />
            <div className="h-3 w-1/3 rounded bg-muted" />
          </div>
        </li>
      ))}
    </ul>
  );
}
